import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getPrograms, getRuns } from "../api/client.js";
import { DetectionTooltip } from "../components/DetectionTooltip.jsx";
import { Shell } from "../components/Shell.jsx";
import { DoughnutChart } from "../components/charts.jsx";
import {
  EvidencePeriodHeader,
  Panel,
  StatTile,
  Loading,
  ErrorCard,
  MetricValue,
  formatCount,
  formatPct,
} from "../components/ui.jsx";
import { ENROLLMENT_DETECTION } from "../opportunityDetection.js";
import { DOMAIN_LABELS } from "../domainModel.js";
import { opportunitiesRoute } from "../navConfig.js";

function LabelWithTooltip({ children, info, label = children, align = "left" }) {
  return (
    <span className="detection-title-row">
      <span>{children}</span>
      <DetectionTooltip label={label} align={align} {...info} />
    </span>
  );
}

function MetricGrainPair({ employees, coveredPeople, showLabels = false }) {
  return (
    <span
      className="metric-grain-pair"
      aria-label={`${formatCount(employees)} employees; ${formatCount(coveredPeople)} covered people`}
    >
      <MetricValue value={employees} grain={showLabels ? "employees" : undefined} />
      <MetricValue value={coveredPeople} grain={showLabels ? "covered people" : undefined} />
    </span>
  );
}

function EnrollmentDonut({ eligible, enrolled, eligibleWithDependents, enrolledWithDependents, title }) {
  const safeEligible = Math.max(eligible || 0, 0);
  const safeEnrolled = Math.min(Math.max(enrolled || 0, 0), safeEligible);
  const safeEligibleWithDependents = Math.max(eligibleWithDependents || safeEligible, 0);
  const safeEnrolledWithDependents = Math.min(
    Math.max(enrolledWithDependents || safeEnrolled, 0),
    safeEligibleWithDependents
  );
  const pct = safeEligible ? safeEnrolled / safeEligible : 0;

  return (
    <div className="enrollment-donut-row">
      <DoughnutChart
        title={`${title} employee enrollment composition`}
        segments={[
          { id: "enrolled", label: "Enrolled employees", value: safeEnrolled, color: "#00B5D6" },
          {
            id: "eligible-not-enrolled",
            label: "Eligible employees not enrolled",
            value: safeEligible - safeEnrolled,
            color: "#058DF0",
          },
        ]}
        centerValue={formatPct(pct, 0)}
        centerLabel="employees enrolled"
        format={formatCount}
        size={164}
      />
      <div className="enrollment-donut-legend">
        <div className="enrollment-column-header" aria-hidden="true">
          <strong>Employees</strong>
          <strong>Covered people</strong>
        </div>
        <div>
          <span className="legend-dot enrolled" />
          <span>Enrolled population</span>
          <MetricGrainPair employees={safeEnrolled} coveredPeople={safeEnrolledWithDependents} />
        </div>
        <div>
          <span className="legend-dot eligible" />
          <span>Eligible, not enrolled</span>
          <MetricGrainPair
            employees={safeEligible - safeEnrolled}
            coveredPeople={safeEligibleWithDependents - safeEnrolledWithDependents}
          />
        </div>
        <div className="eligible-total">
          <span>Total eligible population</span>
          <MetricGrainPair employees={safeEligible} coveredPeople={safeEligibleWithDependents} />
        </div>
      </div>
    </div>
  );
}

export function EnrollmentDashboard({ tenant, auth }) {
  const [programs, setPrograms] = useState(null);
  const [programRuns, setProgramRuns] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    getPrograms()
      .then((programData) => {
        setPrograms(programData.programs);
        return Promise.all(
          programData.programs.map(async (program) => [
            program.programDomain,
            (await getRuns(program.programDomain)).runs,
          ])
        )
          .then((runEntries) => setProgramRuns(Object.fromEntries(runEntries)))
          .catch(() => setProgramRuns({}));
      })
      .catch(setError);
  }, []);

  const body = () => {
    if (error) return <ErrorCard error={error} />;
    if (!programs) return <Loading />;

    const matchedMembers = Math.max(...programs.map((program) => program.eligibleCount || 0));
    const matchedMembersWithDependents = Math.max(
      ...programs.map((program) => program.eligibleWithDependentsCount || program.eligibleCount || 0)
    );
    const enrollOpportunities = programs.reduce(
      (sum, program) => sum + (program.opportunitiesOnNotEnrolledCount || 0),
      0
    );
    const enrollOpportunitiesWithDependents = programs.reduce(
      (sum, program) => sum + (program.opportunitiesOnNotEnrolledWithDependentsCount || 0),
      0
    );
    const usageOpportunities = programs.reduce(
      (sum, program) => sum + (program.opportunitiesOnEnrolledCount || 0),
      0
    );
    const usageOpportunitiesWithDependents = programs.reduce(
      (sum, program) => sum + (program.opportunitiesOnEnrolledWithDependentsCount || 0),
      0
    );
    const largestEnrollmentGap = [...programs].sort(
      (a, b) => (b.opportunitiesOnNotEnrolledCount || 0) - (a.opportunitiesOnNotEnrolledCount || 0)
    )[0];
    const runMonth = programs[0]?.latestRun?.runMonth || null;
    const selectedRunDetails = programs
      .map((program) =>
        programRuns?.[program.programDomain]?.find((run) => run.runMonth === program.latestRun?.runMonth)
      )
      .filter(Boolean);
    const refreshDate = selectedRunDetails
      .map((run) => run.completedAt || run.asOfDate)
      .filter(Boolean)
      .sort()
      .at(-1);

    return (
      <>
        <EvidencePeriodHeader
          period={runMonth}
          refreshedAt={refreshDate}
          freshness={refreshDate ? "current" : "unknown"}
          freshnessLabel={refreshDate ? "Latest available" : undefined}
        />
        <div className="bi-stat-row">
          <StatTile
            k={
              <LabelWithTooltip info={ENROLLMENT_DETECTION.matched}>
                Matched eligible population
              </LabelWithTooltip>
            }
            v={
              <MetricGrainPair
                employees={matchedMembers}
                coveredPeople={matchedMembersWithDependents}
                showLabels
              />
            }
            d="People matched to benefit eligibility; non-matches are excluded"
          />
          <StatTile
            k={<LabelWithTooltip info={ENROLLMENT_DETECTION.goal1}>Enrollment opportunity</LabelWithTooltip>}
            v={
              <MetricGrainPair
                employees={enrollOpportunities}
                coveredPeople={enrollOpportunitiesWithDependents}
                showLabels
              />
            }
            d="Eligible people not enrolled with a current-condition benefit fit"
          />
          <StatTile
            k={
              <LabelWithTooltip info={ENROLLMENT_DETECTION.goal2} align="right">
                Program-use opportunity
              </LabelWithTooltip>
            }
            v={
              <MetricGrainPair
                employees={usageOpportunities}
                coveredPeople={usageOpportunitiesWithDependents}
                showLabels
              />
            }
            d="Enrolled people with a current-condition usage signal"
          />
          <StatTile
            k={
              <LabelWithTooltip info={ENROLLMENT_DETECTION.largestGap} align="right">
                Largest enrollment gap
              </LabelWithTooltip>
            }
            v={
              <MetricGrainPair
                employees={largestEnrollmentGap?.opportunitiesOnNotEnrolledCount}
                coveredPeople={largestEnrollmentGap?.opportunitiesOnNotEnrolledWithDependentsCount}
                showLabels
              />
            }
            d={
              DOMAIN_LABELS[largestEnrollmentGap?.programDomain] ||
              largestEnrollmentGap?.name ||
              "Not enrolled"
            }
          />
        </div>
        <div className="enrollment-program-grid">
          {programs.map((program, programIndex) => {
            const programName = DOMAIN_LABELS[program.programDomain] || program.name;
            const evaluatedOpportunityCount =
              (program.opportunityCount || 0) + (program.suppressedCount || 0);
            const suppressedShare = evaluatedOpportunityCount
              ? (program.suppressedCount || 0) / evaluatedOpportunityCount
              : 0;
            return (
              <Panel key={program.id} title={programName}>
                <EnrollmentDonut
                  title={programName}
                  eligible={program.eligibleCount}
                  enrolled={program.enrolledCount}
                  eligibleWithDependents={program.eligibleWithDependentsCount}
                  enrolledWithDependents={program.enrolledWithDependentsCount}
                />
                <div className="benefit-metric-list">
                  <div>
                    <span>Eligible population</span>
                    <MetricGrainPair
                      employees={program.eligibleCount}
                      coveredPeople={program.eligibleWithDependentsCount}
                    />
                  </div>
                  <div>
                    <span>Enrolled</span>
                    <MetricGrainPair
                      employees={program.enrolledCount}
                      coveredPeople={program.enrolledWithDependentsCount}
                    />
                  </div>
                  <div>
                    <LabelWithTooltip
                      info={ENROLLMENT_DETECTION.goal1}
                      align={programIndex % 3 === 2 ? "right" : "left"}
                    >
                      Enrollment opportunity
                    </LabelWithTooltip>
                    <MetricGrainPair
                      employees={program.opportunitiesOnNotEnrolledCount}
                      coveredPeople={program.opportunitiesOnNotEnrolledWithDependentsCount}
                    />
                  </div>
                  <div>
                    <LabelWithTooltip
                      info={ENROLLMENT_DETECTION.goal2}
                      align={programIndex % 3 === 2 ? "right" : "left"}
                    >
                      Program-use opportunity
                    </LabelWithTooltip>
                    <MetricGrainPair
                      employees={program.opportunitiesOnEnrolledCount}
                      coveredPeople={program.opportunitiesOnEnrolledWithDependentsCount}
                    />
                  </div>
                </div>
                <Link
                  className="program-opportunity-link bi-btn-primary"
                  to={opportunitiesRoute({
                    programDomain: program.programDomain,
                    runMonth: program.latestRun?.runMonth,
                  })}
                >
                  View opportunities
                </Link>
                <div className="program-metrics">
                  <div>
                    <LabelWithTooltip
                      info={ENROLLMENT_DETECTION.enrollmentRate}
                      align={programIndex % 3 === 2 ? "right" : "left"}
                    >
                      Employee enrollment rate
                    </LabelWithTooltip>
                    <strong>{formatPct(program.activationRate)}</strong>
                  </div>
                  <div>
                    <LabelWithTooltip
                      info={ENROLLMENT_DETECTION.candidates}
                      align={programIndex % 3 === 2 ? "right" : "left"}
                    >
                      Detected opportunities
                    </LabelWithTooltip>
                    <MetricValue value={program.opportunityCount} grain="employees" />
                  </div>
                  <div className="high-priority-metric">
                    <LabelWithTooltip
                      info={ENROLLMENT_DETECTION.highPriority}
                      align={programIndex % 3 === 2 ? "right" : "left"}
                    >
                      High priority
                    </LabelWithTooltip>
                    <MetricValue value={program.highPriorityCount} grain="employees" />
                  </div>
                  <div>
                    <LabelWithTooltip
                      info={ENROLLMENT_DETECTION.suppressed}
                      align={programIndex % 3 === 2 ? "right" : "left"}
                    >
                      Suppressed by guardrails
                    </LabelWithTooltip>
                    <span className={`guardrail-value${suppressedShare > 0.2 ? " warning" : ""}`}>
                      <MetricValue value={program.suppressedCount} grain="employees" />
                      <small>{formatPct(suppressedShare)} of evaluated opportunities</small>
                    </span>
                  </div>
                </div>
              </Panel>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <Shell
      tenant={tenant}
      auth={auth}
      activeNav="enrollment"
      title="Enrollment"
      subtitle="Program enrollment compared with the eligible population"
    >
      {body()}
    </Shell>
  );
}
